/**
 * Processes the Emergent Google auth fragment (#session_id=...) once, exchanges
 * it server-side, then drops the signed-in user on the right dashboard.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { Loader2, ShieldAlert } from "lucide-react";
import { ASSETS } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOKEN_KEYS = {
  admin: "grc_admin_token",
  mlo: "grc_mlo_token",
  bdm: "grc_bdm_token",
};

export default function AuthCallback() {
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const sessionId = new URLSearchParams(
      (location.hash || "").replace(/^#/, ""),
    ).get("session_id");

    window.history.replaceState(null, "", window.location.pathname + window.location.search);

    if (!sessionId) {
      setError("That sign-in link is incomplete. Please try again.");
      return;
    }

    axios
      .post(`${API}/auth/google/session`, { session_id: sessionId }, { withCredentials: true })
      .then(({ data }) => {
        const key = TOKEN_KEYS[data.role];
        if (key && data.access_token) localStorage.setItem(key, data.access_token);
        window.location.replace(data.redirect);
      })
      .catch((e) => {
        setError(e?.response?.data?.detail || "Google sign-in failed. Please try again.");
      });
  }, [location.hash]);

  if (error) {
    return (
      <div className="min-h-screen bg-linen flex items-center justify-center px-4 antialiased">
        <div className="w-full max-w-md text-center" data-testid="auth-callback-error">
          <img src={ASSETS.logoHorizontal} alt="Great Rate Check" className="h-10 w-auto mx-auto mb-8" />
          <div className="bg-white border border-linen-border rounded-3xl shadow-soft p-8">
            <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-red-50 text-red-600">
              <ShieldAlert size={22} />
            </span>
            <h1 className="mt-4 font-heading font-medium text-2xl text-navy">Can't sign you in.</h1>
            <p className="mt-3 text-sm text-ink-600 leading-relaxed">{error}</p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="/mlologin"
                data-testid="auth-callback-mlo-login"
                className="inline-flex justify-center items-center bg-navy hover:bg-[#173a5f] text-white font-semibold text-sm px-6 py-3 rounded-full transition-colors"
              >
                Loan officer sign-in
              </a>
              <a
                href="/bdmlogin"
                data-testid="auth-callback-bdm-login"
                className="inline-flex justify-center items-center bg-white border border-linen-border hover:border-teal text-navy font-semibold text-sm px-6 py-3 rounded-full transition-colors"
              >
                Partner sign-in
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-linen flex items-center justify-center antialiased" data-testid="auth-callback">
      <Loader2 className="w-6 h-6 animate-spin text-teal" />
    </div>
  );
}
